import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";
import { Box, TableCell, TableRow } from "@mui/material";
import DotLegalDeleteDialogWrapper from "../../common/components/dotLegalDeleteDialog/DotLegalDeleteDialogWrapper"; // adjust path as needed
import React from "react";
import { useAnnualWheelActivityTemplatesList } from "./AnnualWheelActivityTemplatesList.hooks.ts";
import {
    DotLegalButton,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalTableWithControls,
    DotLegalTooltip,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation.ts";
import { AnnualWheelActivityTemplateTableModel } from "./AnnualWheelActivityTemplatesList.types.ts";
import CustomTemplateAddEditDialog from "./customTemplateAddEditDialog/CustomTemplateAddEditDialog.tsx";
import { AnnualWheelTemplateModel } from "./customTemplateAddEditDialog/CustomTemplateAddEditDialog.types.ts";
import { sortIds } from "../../common/utilities.ts";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender.tsx";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export interface IAnnualWheelActivityTemplatesListProps {
    isObligation?: boolean;
}

function AnnualWheelActivityTemplatesList(props: IAnnualWheelActivityTemplatesListProps) {
    const {
        isLoading,
        hasTagsAccess,
        activityTemplatesTableModel,
        breadCrumbs,
        canManageMasterData,
        onCreateClick,
        createDialogOpen,
        onUpdateClick,
        onDialogClose,
        onSaved,
        templateToUpdate,
        getMenuItems,
        templateForDeletion,
        setTemplateForDeletion,
        onDelete,
        isDeleting,
    } = useAnnualWheelActivityTemplatesList(props);
    const { translateString, translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();

    const headers = Array<ITableHeader<AnnualWheelActivityTemplateTableModel>>();
    headers.push({ property: "customId", text: translateString("id"), sortOverride: (a, b, order) => sortIds(a.customId, b.customId, order === "desc") });
    headers.push({ property: "name", text: translateString("name") });
    headers.push({ property: "description", text: translateString("description") });
    headers.push({ property: "complianceAreaString", text: translateString("area") });
    if (!props.isObligation) {
        headers.push({ property: "generalType", text: translateString("type") });
    }
    headers.push({ property: "notificationPeriodInDays", text: translateString("notificationDays") });
    headers.push({ property: "recurringIntervalString", text: translateString("recurrence") });
    if (props.isObligation) {
        headers.push({ property: "businessAreaString", text: translateString("businessAreas") });
        if (hasTagsAccess) {
            headers.push({ property: "tagsString", text: translateString("tags") });
        }
    }

    const getDefaultTemplate = () => {
        return props.isObligation ? AnnualWheelTemplateModel.createDefaultObligationTemplate() : AnnualWheelTemplateModel.createDefaultTemplate();
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    canManageMasterData ? (
                        <DotLegalButton buttonType="primary" size="medium" onClick={onCreateClick}>
                            {props.isObligation ? translateString("createObligationTemplate") : translateString("createAnnualWheelActivityTemplate")}
                        </DotLegalButton>
                    ) : undefined
                }
            />
            <TablePagePaper>
                <DotLegalTableWithControls
                    internalScroll
                    headers={headers}
                    isLoading={isLoading}
                    data={activityTemplatesTableModel}
                    emptyText={props.isObligation ? translateString("noObligationTemplates") : translateString("noActivityTemplates")}
                    defaultOrderBy={"name"}
                    hideRowsPerPage
                    hasOverflowMenu
                    getUserSpecificPageLength={() => 100}
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    clickableRows={canManageMasterData}
                    renderRow={(row) => (
                        <TableRow key={row.id} onClick={canManageMasterData ? () => onUpdateClick(row.id) : undefined}>
                            <TableCell>{row.customId}</TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                                <DotLegalTooltip text={<MentionInputRender value={row.description ?? ""} linkColor={"primary"} />}>
                                    <Box
                                        sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            width: "250px",
                                            maxWidth: "250px",
                                            minWidth: "250px",
                                            paddingRight: "16px",
                                            height: "1.2em",
                                            // height: "1.5em",
                                            lineHeight: "1.2em",
                                            "& *": {
                                                whiteSpace: "nowrap !important",
                                                display: "inline !important",
                                            },
                                        }}
                                    >
                                        <MentionInputRender value={row.description ?? ""} linkColor={"primary"} />
                                    </Box>
                                </DotLegalTooltip>
                            </TableCell>
                            <TableCell>{row.complianceAreaString}</TableCell>
                            {!props.isObligation && (
                                <TableCell>{row.generalType && translateAnnualWheelActivityGeneralTypeEnum(row.generalType.toString())}</TableCell>
                            )}
                            <TableCell>{row.notificationPeriodInDays}</TableCell>
                            <TableCell>{row.recurringIntervalString}</TableCell>
                            {props.isObligation && (
                                <>
                                    <TableCell>
                                        <ColoredChipsCell items={row.businessAreas} />
                                    </TableCell>
                                    {hasTagsAccess && (
                                        <TableCell>
                                            <ColoredChipsCell items={row.tags} />
                                        </TableCell>
                                    )}
                                </>
                            )}
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>
            {templateForDeletion && (
                <DotLegalDeleteDialogWrapper
                    open
                    onDialogClose={() => setTemplateForDeletion(undefined)}
                    onOkClick={onDelete}
                    itemForDeletion={templateForDeletion.name}
                    inProgress={isDeleting}
                />
            )}
            {(createDialogOpen || templateToUpdate !== undefined) && (
                <CustomTemplateAddEditDialog
                    templateModel={
                        templateToUpdate === undefined
                            ? getDefaultTemplate()
                            : new AnnualWheelTemplateModel(
                                  templateToUpdate.id,
                                  templateToUpdate.name,
                                  templateToUpdate.description,
                                  templateToUpdate.subTasks.map((x) => {
                                      return { name: x, relations: [] };
                                  }),
                                  false,
                                  templateToUpdate.recurringInterval,
                                  templateToUpdate.notificationPeriodInDays,
                                  templateToUpdate.complianceAreas.map((a) => a.id),
                                  templateToUpdate.generalType,
                                  templateToUpdate.customId,
                                  templateToUpdate.recurringIntervalType,
                                  templateToUpdate.startDateRelativeConfig,
                                  templateToUpdate.endDateRelativeConfig,
                                  templateToUpdate.entityTemplates ?? [],
                                  templateToUpdate.businessAreas.map((businessArea) => businessArea.id),
                                  templateToUpdate.tags.map((tag) => tag.id)
                              )
                    }
                    onCancel={onDialogClose}
                    onUpdated={onSaved}
                    onCreated={onSaved}
                    isObligation={props.isObligation}
                />
            )}
        </Box>
    );
}

export default AnnualWheelActivityTemplatesList;
